import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Spinner } from '../ui.jsx';

export function Billing({ toast }) {
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    api('/api/billing/status').then(setStatus),
    api('/api/billing/plans').then(r => setPlans(r.plans||[])),
  ]);

  useEffect(() => { load().catch(()=>{}); }, []);

  const checkout = async (plan) => {
    setBusy(true);
    try {
      const { url } = await api('/api/billing/checkout', { method:'POST', body:{ plan } });
      // In dev (no real Stripe), the mock URL hits /billing/mock-success — auto-confirm instead so we don't bounce to a 404.
      if (url && url.includes('/billing/mock-success')) {
        await api('/api/billing/mock-confirm', { method:'POST', body:{ plan } });
        await load();
        toast('Subscription activated (mock)', 'success');
      } else if (url) {
        window.location.href = url;
      }
    } catch (e) {
      toast(e.message, 'error');
    } finally { setBusy(false); }
  };

  const portal = async () => {
    try {
      const { url } = await api('/api/billing/portal', { method:'POST' });
      if (url) window.location.href = url;
    } catch (e) { toast(e.message, 'error'); }
  };

  const cancel = async () => {
    if (!confirm('Cancel subscription? You\'ll keep access until the end of the period.')) return;
    try {
      await api('/api/billing/cancel', { method:'POST' });
      await load();
      toast('Subscription canceled', 'success');
    } catch (e) { toast(e.message, 'error'); }
  };

  if (!status) return <div style={{ padding:40 }}><Spinner /></div>;

  // Agency mode: super-admin not ghosted into any sub-account. Billing belongs
  // to a specific account so there's nothing to show — point them at the Agency Console.
  if (status.noAccountContext || status.status === 'agency_mode') {
    return <div style={{ padding:40, maxWidth:720, margin:'0 auto' }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>💳 Billing</h1>
      <div style={{ ...styles.card, padding:24, marginTop:18, lineHeight:1.6 }}>
        <div style={{ fontSize:15, marginBottom:8 }}>You're in <strong>agency mode</strong>.</div>
        <div style={{ color:G.textMuted, fontSize:14 }}>Billing lives per sub-account. Ghost into the account you want to manage from the Agency Console, then come back here to see its plan and invoices.</div>
      </div>
    </div>;
  }

  const trialing = status.status === 'trialing';
  const active = status.status === 'active';
  const pastDue = status.status === 'past_due';
  const canceled = status.status === 'canceled';

  const banner = trialing ? { c:G.cyan, t:`Free trial — ${status.trialDaysLeft} day${status.trialDaysLeft===1?'':'s'} left. Add a card to keep your account after the trial.` }
    : pastDue ? { c:G.red, t:'Payment failed — update your card to restore access.' }
    : canceled ? { c:G.textMuted, t:`Canceled${status.canceledAt?` on ${new Date(status.canceledAt).toLocaleDateString()}`:''}. Pick a plan below to reactivate.` }
    : null;

  return <div style={{ padding:'24px 28px', maxWidth:1100 }}>
    <h2 style={{ marginTop:0 }}>Billing</h2>

    {banner && <div style={{ padding:14, borderRadius:10, background:G.input, border:`1px solid ${banner.c}`, color:banner.c, marginBottom:18, fontWeight:600 }}>{banner.t}</div>}

    <div style={{ ...styles.card, padding:20, marginBottom:18 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:14 }}>
        <div>
          <div style={{ fontSize:12, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em' }}>Current Plan</div>
          <div style={{ fontSize:26, fontWeight:800, color:G.text, marginTop:4 }}>{status.plan} <span style={{ fontSize:16, color:G.textMuted, fontWeight:500 }}>· ${status.mrr}/mo</span></div>
          <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>
            Status: <strong style={{ color: active?G.green : trialing?G.cyan : G.red }}>{status.status}</strong>
            {status.currentPeriodEnd && <> · Renews {new Date(status.currentPeriodEnd).toLocaleDateString()}</>}
          </div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          {status.hasPaymentMethod && !canceled && <Btn v="ghost" onClick={portal}>Manage Card</Btn>}
          {active && <Btn v="ghost" onClick={cancel}>Cancel</Btn>}
        </div>
      </div>
    </div>

    <CardOnFile status={status} onSaved={load} toast={toast}/>

    <UsagePanel toast={toast}/>

    <h3>{canceled ? 'Reactivate' : trialing ? 'Add a card to keep your account' : 'Change Plan'}</h3>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:14 }}>
      {plans.map(p => {
        const current = status.planKey === p.key && active;
        return <div key={p.key} style={{
          ...styles.card, padding:18, position:'relative',
          border: current ? `2px solid ${G.green}` : (p.popular ? `2px solid ${G.purple}` : `1px solid ${G.border}`),
        }}>
          {p.popular && !current && <div style={{ position:'absolute', top:-10, right:14, background:G.purple, color:'#fff', fontSize:11, padding:'2px 8px', borderRadius:10, fontWeight:700 }}>POPULAR</div>}
          {current && <div style={{ position:'absolute', top:-10, right:14, background:G.green, color:'#fff', fontSize:11, padding:'2px 8px', borderRadius:10, fontWeight:700 }}>CURRENT</div>}
          <div style={{ fontWeight:800, fontSize:18 }}>{p.name}</div>
          <div style={{ marginTop:6 }}><span style={{ fontSize:28, fontWeight:800 }}>${p.price}</span><span style={{ color:G.textMuted, fontSize:13 }}>/mo</span></div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:6, lineHeight:1.4 }}>{p.blurb}</div>
          <ul style={{ marginTop:12, padding:0, listStyle:'none', fontSize:12, lineHeight:1.7 }}>
            {p.features?.map((f,i) => <li key={i}>✓ {f}</li>)}
          </ul>
          {!current && <Btn v="primary" sz="sm" full onClick={()=>checkout(p.key)} disabled={busy} style={{ marginTop:12 }}>
            {busy ? <Spinner/> : (trialing || canceled ? `Choose ${p.name}` : `Switch to ${p.name}`)}
          </Btn>}
        </div>;
      })}
    </div>
  </div>;
}

// Lazily load Stripe.js from CDN once per session — avoids an npm dep for a single widget.
let stripeJsPromise = null;
function loadStripe() {
  if (stripeJsPromise) return stripeJsPromise;
  stripeJsPromise = new Promise((resolve, reject) => {
    if (window.Stripe) return resolve(window.Stripe);
    const s = document.createElement('script');
    s.src = 'https://js.stripe.com/v3';
    s.onload = () => resolve(window.Stripe);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return stripeJsPromise;
}

function CardOnFile({ status, onSaved, toast }) {
  const [open, setOpen] = useState(false);
  const [stripeInst, setStripeInst] = useState(null);
  const [elements, setElements] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  const cardRef = React.useRef(null);

  const start = async () => {
    setOpen(true); setBusy(true);
    try {
      const r = await api('/api/billing/setup-intent', { method:'POST' });
      setClientSecret(r.clientSecret);
      // Stripe Elements requires the publishable key — embedded in window for now
      const Stripe = await loadStripe();
      // Fetch pk_test_/pk_live_ from server config endpoint (not yet built — fall back to env in window if injected)
      const pk = window.__STRIPE_PK__ || (await api('/api/billing/public-key').catch(()=>({})))?.publishableKey;
      if (!pk) { toast('Stripe publishable key not configured', 'error'); setBusy(false); return; }
      const stripe = Stripe(pk);
      const el = stripe.elements({ clientSecret: r.clientSecret, appearance: { theme: 'night' }});
      const card = el.create('payment');
      setStripeInst(stripe); setElements(el);
      setTimeout(() => { card.mount(cardRef.current); }, 100);
    } catch (e) { toast(e.message, 'error'); setOpen(false); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!stripeInst || !elements) return;
    setBusy(true);
    try {
      const result = await stripeInst.confirmSetup({ elements, redirect: 'if_required' });
      if (result.error) throw new Error(result.error.message);
      const setupIntent = result.setupIntent;
      if (setupIntent?.payment_method) {
        await api('/api/billing/save-payment-method', { method:'POST', body:{ paymentMethodId: setupIntent.payment_method }});
        toast('Card saved');
        setOpen(false);
        onSaved();
      }
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (status.hasCardOnFile && !open) {
    return <div style={{ ...styles.card, padding:18, marginBottom:14, borderColor:G.green+'66' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <strong>💳 Card on file</strong>
          <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>
            We use this to bill recurring subscription + any usage overage (SMS, voice minutes, phone-number rental).
          </div>
        </div>
        <Btn v="ghost" onClick={start}>Update Card</Btn>
      </div>
    </div>;
  }

  if (!open) {
    return <div style={{ ...styles.card, padding:18, marginBottom:14, borderColor:G.yellow+'66' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:14 }}>
        <div>
          <strong>💳 Add a card on file</strong>
          <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>
            Required before connecting Stripe Connect or sending SMS at scale. We bill subscriptions + any usage overage from this card.
          </div>
        </div>
        <Btn v="primary" onClick={start} disabled={busy}>{busy ? <Spinner sz={14}/> : 'Add Card'}</Btn>
      </div>
    </div>;
  }

  return <div style={{ ...styles.card, padding:18, marginBottom:14 }}>
    <strong>Add a payment method</strong>
    <div ref={cardRef} style={{ marginTop:14, padding:12, background:G.input, borderRadius:8, minHeight:140 }}/>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:14 }}>
      <Btn onClick={()=>setOpen(false)}>Cancel</Btn>
      <Btn v="primary" onClick={save} disabled={busy}>{busy ? <Spinner sz={14}/> : 'Save Card'}</Btn>
    </div>
  </div>;
}

// ─── Month-to-date usage + threshold control ─────────────────────────────────
function UsagePanel({ toast }) {
  const [u, setU] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(50);
  const load = () => api('/api/billing/usage').then(r => { setU(r); setDraft(r.overage?.thresholdUsd ?? 50); });
  useEffect(() => { load(); }, []);
  if (!u) return null;
  const o = u.overage || {};
  const pct = Math.min(o.pctUsed || 0, 100);
  const barColor = o.exceeded ? G.red : o.approaching ? G.yellow : G.green;
  const save = async () => {
    try {
      await api('/api/billing/usage-threshold', { method:'PATCH', body:{ cents: Math.round(Number(draft) * 100) }});
      await load();
      setEditing(false);
      toast('Threshold updated');
    } catch(e) { toast(e.message,'error'); }
  };
  return <div style={{ ...styles.card, padding:18, marginBottom:18 }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
      <strong>📊 Usage this month</strong>
      <div style={{ fontSize:14 }}>
        <strong style={{ color:barColor }}>${u.monthToDate.totalBilledUsd.toFixed(2)}</strong>
        <span style={{ color:G.textMuted }}> of ${o.thresholdUsd ?? 0} threshold</span>
      </div>
    </div>
    <div style={{ height:8, background:G.input, borderRadius:4, overflow:'hidden', marginBottom:6 }}>
      <div style={{ width:`${pct}%`, height:'100%', background:barColor, transition:'.3s' }}/>
    </div>
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:G.textMuted }}>
      <span>{pct}% used</span>
      {o.exceeded && <span style={{ color:G.red, fontWeight:600 }}>Overage will bill on next invoice</span>}
    </div>

    {/* Threshold edit */}
    <div style={{ marginTop:14, padding:12, background:G.input, borderRadius:8 }}>
      {!editing ? (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:12, color:G.textMuted }}>Soft threshold (warning at 80%)</div>
            <div style={{ fontWeight:700, fontSize:15, marginTop:2 }}>${o.thresholdUsd ?? 0}/month</div>
          </div>
          <Btn sz="sm" onClick={()=>setEditing(true)}>Edit</Btn>
        </div>
      ) : (
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <span style={{ fontSize:14 }}>$</span>
          <input type="number" value={draft} onChange={e=>setDraft(e.target.value)} min="0" step="10"
            style={{ flex:1, padding:'8px 10px', background:G.bg, border:`1px solid ${G.cardBorder}`, borderRadius:6, color:G.text, fontSize:14 }}/>
          <Btn sz="sm" v="primary" onClick={save}>Save</Btn>
          <Btn sz="sm" onClick={()=>{ setDraft(o.thresholdUsd ?? 0); setEditing(false); }}>Cancel</Btn>
        </div>
      )}
      <div style={{ fontSize:11, color:G.textMuted, marginTop:8 }}>
        We warn you at 80% and again when you cross this amount. Hard cap is 10× this (${(o.thresholdUsd ?? 0) * 10}) — calls pause until next month or you raise it.
      </div>
    </div>

    {/* Breakdown */}
    {u.monthToDate.breakdown?.length > 0 && (
      <div style={{ marginTop:14 }}>
        <div style={{ fontSize:12, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>Breakdown</div>
        <table style={{ width:'100%', fontSize:13 }}>
          <tbody>
            {u.monthToDate.breakdown.map((b, i) => (
              <tr key={i}>
                <td style={{ padding:'6px 0', color:G.text }}>{b.kind} {b.model ? `(${b.model})` : ''}</td>
                <td style={{ textAlign:'right', color:G.textMuted }}>×{b.count}</td>
                <td style={{ textAlign:'right', padding:'6px 0', fontWeight:600 }}>${b.billedUsd.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Rate card */}
    <details style={{ marginTop:14, fontSize:12, color:G.textMuted }}>
      <summary style={{ cursor:'pointer', fontWeight:600 }}>Pay-as-you-go rates</summary>
      <div style={{ marginTop:8, lineHeight:1.7 }}>
        AI voice: ${u.rates?.voice_ai_per_min_usd ?? '—'}/min · Browser call: ${u.rates?.voice_per_min_usd ?? '—'}/min · SMS: ${u.rates?.sms_per_segment_usd ?? '—'}/segment<br/>
        AI image (standard): ${u.rates?.image_standard_usd ?? '—'} · (premium): ${u.rates?.image_premium_usd ?? '—'}<br/>
        AI video (short): ${u.rates?.video_short_usd ?? '—'} · (HD): ${u.rates?.video_hd_usd ?? '—'} · (premium): ${u.rates?.video_premium_usd ?? '—'}
      </div>
    </details>
  </div>;
}
